import { Flex, Stepper, Button } from '@servicetitan/anvil2';
import { ImperativeForm } from '@servicetitan/financing-application-shared';
import {
    SetupDataStore,
    useProviderConfig,
    WorkflowButtonsStore,
    WorkflowStore,
} from '@servicetitan/financing-workflow-shared';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import {
    isActivePreApprovalSubmission,
    PlansEntryReason,
    PlansListTelemetryStore,
} from '@servicetitan/financing-utils-shared';
import { RefObject, useCallback } from 'react';

interface ApplicationButtonsProps {
    formRef: RefObject<ImperativeForm>;
    isNextButtonDisabled?: boolean;
    showBackToPlanSelection?: boolean;
}

export const ApplicationButtons = ({
    formRef,
    isNextButtonDisabled,
    showBackToPlanSelection = false,
}: ApplicationButtonsProps) => {
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setNextEntryReason } = PlansListTelemetryStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const {
        useCustomerAuthFlow,
        useFinancingSubmission,
        useFinancingPrequalSubmission,
        continueToHardAppAfterSoft,
        defaultWorkflowStage,
        submitButtonLabel: configSubmitButtonLabel,
    } = useProviderConfig();

    // Soft-prequal UI mode covers both legacy prequals-API soft and financing soft prequal.
    const isSoftPrequalUiMode =
        useFinancingPrequalSubmission === true || useFinancingSubmission === false;

    // Determine if we should use "Submit" button (soft prequal) vs "Submit Application" (hard prequal):
    // 1. Must be in soft-prequal UI mode
    // 2. For prequals-landing flow (continueToHardAppAfterSoft: false): always use "Submit"
    // 3. For continue-to-hard flow (continueToHardAppAfterSoft: true): only use "Submit" during soft phase
    const shouldUseSoftPrequalButton = () => {
        // If the applicant is pre-approved, we should skip the soft app step.
        if (isActivePreApprovalSubmission(setupData)) {
            return false;
        }
        if (!isSoftPrequalUiMode) {
            return false;
        }
        // Soft→hard transition complete: hard credit application submit label
        if (setupData?.hasTransitionedToHardApp) {
            return false;
        }
        // Prequals-landing flow: always use "Submit" (no hard phase)
        if (!continueToHardAppAfterSoft) {
            return true;
        } else if (!setupData?.prequalificationId && !setupData?.loanId) {
            // Soft prequal flow (GreenSky prequals API or financing soft prequal)
            return true;
        }
        // Continue-to-hard flow: use "Submit" only during soft phase
        // (isApplyingForPrequalification must be explicitly true - undefined means hard app from banner)
        return setupData?.isApplyingForPrequalification === true;
    };

    const computedLabel = shouldUseSoftPrequalButton() ? 'Submit' : 'Submit Application';
    const submitButtonLabel = configSubmitButtonLabel ?? computedLabel;

    const nextHandler = useCallback(async () => {
        if (isNextButtonDisabled) {
            return false;
        }
        // Prefer submit()'s return value over isValid() — formState.isValid can
        // briefly report stale true for untouched required fields after handleSubmit.
        const isValid = await formRef.current?.submit();
        return isValid ?? true;
    }, [formRef, isNextButtonDisabled]);

    const { CancelApplicationButton } = WorkflowButtonsStore.useStore();

    return (
        <Flex justifyContent="space-between">
            {showBackToPlanSelection ? (
                <Button
                    appearance="secondary"
                    onClick={() => {
                        if (
                            setupData?.role === 'homeowner' &&
                            useCustomerAuthFlow &&
                            !setupData?.hasTransitionedToHardApp
                        ) {
                            setNextEntryReason(PlansEntryReason.BackNavigation);
                            setWorkflowStage('customer-auth');
                            return;
                        }

                        setNextEntryReason(PlansEntryReason.BackNavigation);
                        setWorkflowStage(defaultWorkflowStage);
                    }}
                >
                    Back
                </Button>
            ) : (
                <Stepper.PrevButton label="Back" />
            )}
            <Flex gap="3" justifyContent="flex-end" grow={1}>
                <CancelApplicationButton>Cancel</CancelApplicationButton>
                <Stepper.NextButton
                    nextLabel="Next"
                    completeLabel={submitButtonLabel}
                    disabled={
                        isNextButtonDisabled ??
                        (selectedPlan == null && !setupData?.isApplyingForPrequalification)
                    }
                    onClick={nextHandler as unknown as () => void}
                />
            </Flex>
        </Flex>
    );
};
